import { useState } from 'react';
import { BudgetRequestListItemDto } from '../../../types';
import { useBrgList } from '../../../hooks/useBrgList';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../../api/client';
import { UserDropdownDto } from '../../../types';
import { DateInputDDMMYYYY } from '../../../components/shared/DateInputDDMMYYYY';

interface BudgetRequestEditModalProps {
  request: BudgetRequestListItemDto;
  onClose: () => void;
  onSave: (updatedData: any) => void;
  isLoading?: boolean;
}

function formatScheduledDate(dateString?: string): string {
  if (!dateString) return '';
  return dateString;
}

export function BudgetRequestEditModal({
  request,
  onClose,
  onSave,
  isLoading = false,
}: BudgetRequestEditModalProps) {
  const { data: brgs = [] } = useBrgList();
  // Fetch all users (including inactive) to ensure we can resolve all requesters
  const { data: users = [] } = useQuery({
    queryKey: ['users', 'all'],
    queryFn: () => apiClient.get<UserDropdownDto[]>('/api/users?active=false'),
  });
  
  // Find user name from dynamic data
  const submittedBy = users.find(u => u.userId === request.requestedById)?.displayName || '—';

  const [formData, setFormData] = useState({
    brgId: request.brgId,
    requestAmount: request.requestAmount || 0,
    projectEvent: request.projectEvent || '',
    scheduledDate: formatScheduledDate(request.scheduledDate) || '',
    purposeDescription: request.purposeDescription || '',
    requestDescription: request.requestDescription || '',
    impact: request.impact || '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'requestAmount' || name === 'brgId' ? Number(value) : value,
    }));
  };

  const handleSave = () => {
    onSave(formData);
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(20, 30, 50, 0.55)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          width: '480px',
          maxWidth: '95vw',
          maxHeight: '88vh',
          overflowY: 'auto',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
        }}
      >
        {/* Header */}
        <div
          style={{
            backgroundColor: '#1a3bcc',
            color: 'white',
            padding: '16px 22px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <h2 style={{ fontSize: '15px', fontWeight: 800, margin: 0 }}>Edit Budget Request</h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: 'white',
              fontSize: '20px',
              cursor: 'pointer',
              padding: '0 4px',
              fontWeight: 700,
            }}
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '18px 22px', maxHeight: '60vh', overflowY: 'auto' }}>
          {/* BRG Information Section */}
          <div style={{ marginBottom: '16px' }}>
            <div
              style={{
                fontSize: '10px',
                fontWeight: 800,
                color: '#888',
                letterSpacing: '1px',
                textTransform: 'uppercase',
                borderBottom: '1px solid #eee',
                paddingBottom: '5px',
                marginBottom: '12px',
              }}
            >
              BRG Information
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label
                style={{
                  fontSize: '11px',
                  fontWeight: 700,
                  color: '#444',
                  marginBottom: '4px',
                  display: 'block',
                }}
              >
                BRG Name <span style={{ color: '#cc1a1a' }}>*</span>
              </label>
              <select
                name="brgId"
                value={formData.brgId}
                onChange={handleChange}
                style={{
                  width: '100%',
                  background: '#fff',
                  border: '1px solid #c0c0c0',
                  borderRadius: '4px',
                  padding: '8px 12px',
                  fontSize: '12px',
                  color: '#222',
                  fontFamily: 'Arial, sans-serif',
                  outline: 'none',
                  cursor: 'pointer',
                }}
                onFocus={(e) => (e.target.style.borderColor = '#1a3bcc')}
                onBlur={(e) => (e.target.style.borderColor = '#c0c0c0')}
              >
                {brgs.map((brg) => (
                  <option key={brg.brgId} value={brg.brgId}>
                    {brg.name}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label
                style={{
                  fontSize: '11px',
                  fontWeight: 700,
                  color: '#444',
                  marginBottom: '4px',
                  display: 'block',
                }}
              >
                Submitted By <span style={{ color: '#cc1a1a' }}>*</span>
              </label>
              <div
                style={{
                  background: '#f8f9fb',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  padding: '8px 12px',
                  fontSize: '12px',
                  color: '#222',
                }}
              >
                {submittedBy}
              </div>
            </div>
          </div>

          {/* Request Details Section */}
          <div>
            <div
              style={{
                fontSize: '10px',
                fontWeight: 800,
                color: '#888',
                letterSpacing: '1px',
                textTransform: 'uppercase',
                borderBottom: '1px solid #eee',
                paddingBottom: '5px',
                marginBottom: '12px',
              }}
            >
              Request Details
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label
                style={{
                  fontSize: '11px',
                  fontWeight: 700,
                  color: '#444',
                  marginBottom: '4px',
                  display: 'block',
                }}
              >
                Request Amount <span style={{ color: '#cc1a1a' }}>*</span>
              </label>
              <div style={{ display: 'flex', alignItems: 'stretch' }}>
                <div
                  style={{
                    background: '#eef0f4',
                    border: '1px solid #ddd',
                    borderRight: 'none',
                    borderRadius: '4px 0 0 4px',
                    padding: '8px 10px',
                    fontSize: '12px',
                    color: '#555',
                    fontWeight: 700,
                  }}
                >
                  $
                </div>
                <input
                  type="number"
                  name="requestAmount"
                  value={formData.requestAmount}
                  onChange={handleChange}
                  max={100000}
                  style={{
                    flex: 1,
                    border: '1px solid #c0c0c0',
                    borderRadius: '0 4px 4px 0',
                    padding: '8px 12px',
                    fontSize: '12px',
                    fontFamily: 'Arial, sans-serif',
                    background: '#fff',
                    outline: 'none',
                  }}
                  onFocus={(e) => (e.target.style.borderColor = '#1a3bcc')}
                  onBlur={(e) => (e.target.style.borderColor = '#c0c0c0')}
                />
              </div>
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label
                style={{
                  fontSize: '11px',
                  fontWeight: 700,
                  color: '#444',
                  marginBottom: '4px',
                  display: 'block',
                }}
              >
                Project / Event
                <span style={{ fontWeight: 400, color: '#999', fontSize: '10px', marginLeft: '4px' }}>
                  (optional)
                </span>
              </label>
              <input
                type="text"
                name="projectEvent"
                value={formData.projectEvent}
                onChange={handleChange}
                style={{
                  width: '100%',
                  border: '1px solid #c0c0c0',
                  borderRadius: '4px',
                  padding: '8px 12px',
                  fontSize: '12px',
                  fontFamily: 'Arial, sans-serif',
                  background: '#fff',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
                onFocus={(e) => (e.target.style.borderColor = '#1a3bcc')}
                onBlur={(e) => (e.target.style.borderColor = '#c0c0c0')}
              />
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label
                style={{
                  fontSize: '11px',
                  fontWeight: 700,
                  color: '#444',
                  marginBottom: '4px',
                  display: 'block',
                }}
              >
                Scheduled Date
                <span style={{ fontWeight: 400, color: '#999', fontSize: '10px', marginLeft: '4px' }}>
                  (optional)
                </span>
              </label>
              <DateInputDDMMYYYY
                value={formData.scheduledDate}
                onChange={(val) => handleChange({ target: { name: 'scheduledDate', value: val } } as React.ChangeEvent<HTMLInputElement>)}
                style={{
                  width: '100%',
                  border: '1px solid #c0c0c0',
                  borderRadius: '4px',
                  padding: '8px 12px',
                  fontSize: '12px',
                  fontFamily: 'Arial, sans-serif',
                  background: '#fff',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label
                style={{
                  fontSize: '11px',
                  fontWeight: 700,
                  color: '#444',
                  marginBottom: '4px',
                  display: 'block',
                }}
              >
                Purpose / Description <span style={{ color: '#cc1a1a' }}>*</span>
              </label>
              <input
                type="text"
                name="purposeDescription"
                value={formData.purposeDescription}
                onChange={handleChange}
                style={{
                  width: '100%',
                  border: '1px solid #c0c0c0',
                  borderRadius: '4px',
                  padding: '8px 12px',
                  fontSize: '12px',
                  fontFamily: 'Arial, sans-serif',
                  background: '#fff',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
                onFocus={(e) => (e.target.style.borderColor = '#1a3bcc')}
                onBlur={(e) => (e.target.style.borderColor = '#c0c0c0')}
              />
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label
                style={{
                  fontSize: '11px',
                  fontWeight: 700,
                  color: '#444',
                  marginBottom: '4px',
                  display: 'block',
                }}
              >
                Request Description <span style={{ color: '#cc1a1a' }}>*</span>
              </label>
              <textarea
                name="requestDescription"
                value={formData.requestDescription}
                onChange={handleChange}
                style={{
                  width: '100%',
                  border: '1px solid #c0c0c0',
                  borderRadius: '4px',
                  padding: '8px 12px',
                  fontSize: '12px',
                  fontFamily: 'Arial, sans-serif',
                  background: '#fff',
                  minHeight: '60px',
                  resize: 'vertical',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
                onFocus={(e) => (e.target.style.borderColor = '#1a3bcc')}
                onBlur={(e) => (e.target.style.borderColor = '#c0c0c0')}
              />
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label
                style={{
                  fontSize: '11px',
                  fontWeight: 700,
                  color: '#444',
                  marginBottom: '4px',
                  display: 'block',
                }}
              >
                Impact <span style={{ color: '#cc1a1a' }}>*</span>
              </label>
              <textarea
                name="impact"
                value={formData.impact}
                onChange={handleChange}
                placeholder="Describe how these funds will benefit the BRG, its members, or the broader organization..."
                style={{
                  width: '100%',
                  border: '1px solid #c0c0c0',
                  borderRadius: '4px',
                  padding: '8px 12px',
                  fontSize: '12px',
                  fontFamily: 'Arial, sans-serif',
                  background: '#fff',
                  minHeight: '60px',
                  resize: 'vertical',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
                onFocus={(e) => (e.target.style.borderColor = '#1a3bcc')}
                onBlur={(e) => (e.target.style.borderColor = '#c0c0c0')}
              />
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '10px',
            padding: '14px 22px 20px',
            borderTop: '1px solid #eee',
          }}
        >
          <button
            onClick={onClose}
            style={{
              background: '#fff',
              color: '#444',
              border: '1px solid #ccc',
              borderRadius: '4px',
              padding: '9px 22px',
              fontSize: '13px',
              fontWeight: 700,
              cursor: 'pointer',
            }}
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            style={{
              background: '#1a7c3a',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              padding: '9px 24px',
              fontSize: '13px',
              fontWeight: 700,
              cursor: isLoading ? 'not-allowed' : 'pointer',
              opacity: isLoading ? 0.6 : 1,
            }}
            disabled={isLoading}
          >
            {isLoading ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
